import React, {useState} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {ACCESS_LABELS} from './constants'
import {shareErrorMessage} from './errors'
import {accessLabel, formatShareDate, isPast} from './format'
import {
  useRemoveShareMember,
  useRevokeShareInvitation,
  useUpdateShareMember,
} from './managementHooks'
import type {MemberAccess, ShareInvitation, ShareMember} from './types'

interface ShareMembersListProps {
  shareId: string
  members: ShareMember[]
  /** Sub of the signed-in user, labelled "you". */
  currentSub?: string
}

/** Members with their access, who invited them and when they joined; change access or remove. */
export const ShareMembersList: React.FC<ShareMembersListProps> = ({
  shareId,
  members,
  currentSub,
}) => {
  const updateMember = useUpdateShareMember(shareId)
  const removeMember = useRemoveShareMember(shareId)
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)
  const [busySub, setBusySub] = useState<string | null>(null)

  const nameOf = (sub: string | undefined) => {
    if (!sub) return ''
    const m = members.find((x) => x.sub === sub)
    return m ? m.name || m.email || '' : ''
  }

  const changeAccess = (member: ShareMember, access: MemberAccess) => {
    setBusySub(member.sub)
    updateMember.mutate(
      {sub: member.sub, access},
      {
        onSuccess: () =>
          toast.success(
            `${member.name || member.email || 'Member'} now has ${ACCESS_LABELS[access]} access`,
            toastDefaultConfig
          ),
        onError: (err) => {
          toast.error(shareErrorMessage(err), toastDefaultConfig)
          getLogger().warn('Change of share access failed', err)
        },
        onSettled: () => setBusySub(null),
      }
    )
  }

  const remove = (member: ShareMember) => {
    setBusySub(member.sub)
    removeMember.mutate(member.sub, {
      onSuccess: () => {
        toast.success(`${member.name || member.email || 'Member'} was removed`, toastDefaultConfig)
        setConfirmRemove(null)
      },
      onError: (err) => {
        toast.error(shareErrorMessage(err), toastDefaultConfig)
        getLogger().warn('Removing a share member failed', err)
      },
      onSettled: () => setBusySub(null),
    })
  }

  return (
    <ul className='list-unstyled mb-0 ob-sharing-list' aria-label='Members'>
      {members.map((member) => {
        const isOwner = member.access === 'owner'
        const busy = busySub === member.sub
        const label = member.name || member.email || 'Unknown user'
        const inviter = nameOf(member.invitedBy)
        return (
          <li key={member.sub} className='ob-sharing-list__row' data-testid='sharing-member-row'>
            <div className='ob-sharing-list__main'>
              <div className='fw-semibold text-truncate'>
                {label}
                {member.sub === currentSub && <span className='text-muted fw-normal'> (you)</span>}
              </div>
              {member.email && member.name && (
                <div className='text-muted small text-truncate'>{member.email}</div>
              )}
              {!isOwner && (
                <div className='text-muted small'>
                  {inviter ? `Invited by ${inviter} · ` : ''}Added {formatShareDate(member.addedAt)}
                  {member.admittedVia === 'external' && ' · Outside your subscriptions'}
                </div>
              )}
            </div>
            <div className='ob-sharing-list__actions'>
              {isOwner ? (
                <span className='text-muted small px-2'>{ACCESS_LABELS.owner}</span>
              ) : confirmRemove === member.sub ? (
                <div className='d-flex align-items-center gap-2' role='group'>
                  <span className='small'>Remove {label}?</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-danger'
                    onClick={() => remove(member)}
                    disabled={busy}
                  >
                    {busy ? 'Removing…' : 'Remove'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => setConfirmRemove(null)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <select
                    className='form-select form-select-sm w-auto'
                    aria-label={`Access for ${label}`}
                    value={member.access}
                    disabled={busy}
                    onChange={(e) => changeAccess(member, e.target.value as MemberAccess)}
                  >
                    <option value='view'>{ACCESS_LABELS.view}</option>
                    <option value='edit'>{ACCESS_LABELS.edit}</option>
                  </select>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light'
                    title={`Remove ${label}`}
                    aria-label={`Remove ${label}`}
                    onClick={() => setConfirmRemove(member.sub)}
                    disabled={busy}
                  >
                    <i className='fas fa-user-minus' aria-hidden='true' />
                  </button>
                </>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

interface SharePendingInvitationsProps {
  shareId: string
  invitations: ShareInvitation[]
}

/** Invitations sent but not answered yet, with Withdraw. */
export const SharePendingInvitations: React.FC<SharePendingInvitationsProps> = ({
  shareId,
  invitations,
}) => {
  const revoke = useRevokeShareInvitation(shareId)
  const [busyId, setBusyId] = useState<string | null>(null)

  const withdraw = (invite: ShareInvitation) => {
    setBusyId(invite.inviteId)
    revoke.mutate(invite.inviteId, {
      onSuccess: () => toast.success(`Invitation to ${invite.email} withdrawn`, toastDefaultConfig),
      onError: (err) => {
        toast.error(shareErrorMessage(err), toastDefaultConfig)
        getLogger().warn('Withdrawing a share invitation failed', err)
      },
      onSettled: () => setBusyId(null),
    })
  }

  if (invitations.length === 0) return null

  return (
    <ul className='list-unstyled mb-0 ob-sharing-list' aria-label='Pending invitations'>
      {invitations.map((invite) => {
        const expired = invite.status === 'expired' || isPast(invite.expiresAt)
        return (
          <li
            key={invite.inviteId}
            className='ob-sharing-list__row'
            data-testid='sharing-invitation-row'
          >
            <div className='ob-sharing-list__main'>
              <div className='fw-semibold text-truncate'>{invite.email}</div>
              <div className='text-muted small'>
                {accessLabel(invite.access)} ·{' '}
                {expired ? (
                  <span className='badge badge-light-warning'>Expired</span>
                ) : (
                  `Expires ${formatShareDate(invite.expiresAt)}`
                )}
              </div>
            </div>
            <div className='ob-sharing-list__actions'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => withdraw(invite)}
                disabled={busyId === invite.inviteId}
                aria-label={`Withdraw the invitation to ${invite.email}`}
              >
                {busyId === invite.inviteId ? 'Withdrawing…' : 'Withdraw'}
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
